import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LockKey } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { getHomePathForRole } from "../modules/registry";
import { LOGO_URL, PORTAL_NAME } from "../constants/brand";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageWatermark } from "../components/shared/PageWatermark";

export default function SetPasswordPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/set-initial-password", { new_password: newPassword });
      toast.success("Password set — welcome to IGC");
      const freshUser = await refreshUser();
      navigate(getHomePathForRole(freshUser?.role, freshUser?.unlocked_modules), { replace: true });
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-auth-bg flex items-center justify-center p-6 relative overflow-hidden" data-testid="set-password-page">
      <PageWatermark variant="auth" />
      <div className="w-full max-w-sm bg-auth-surface border border-auth-gold/20 p-10 relative z-[1]">
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="Iron Gate Consulting" className="w-20 h-20 object-contain mb-3 auth-glow" />
          <p className="font-serif text-auth-cream text-sm tracking-wide">{PORTAL_NAME}</p>
        </div>
        <h1 className="font-serif text-xl text-auth-cream text-center mb-2">Set Your Password</h1>
        <p className="text-auth-muted text-sm text-center mb-8">
          Welcome, {user?.client_id || user?.display_name}. Please set a permanent password to continue.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <Input
            data-testid="set-password-new-input"
            type="password"
            required
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            title="Choose a permanent password — must be at least 8 characters. This replaces your temporary one."
            className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-12"
          />
          <Input
            data-testid="set-password-confirm-input"
            type="password"
            required
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            title="Type the same password again to confirm you typed it correctly."
            className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-12"
          />
          <Button data-testid="set-password-submit-button" type="submit" disabled={submitting} title="Saves your permanent password and takes you into your dashboard." className="w-full h-12 bg-auth-gold hover:bg-auth-goldHover text-black">
            {submitting ? "Saving..." : "Set Password & Continue"}
          </Button>
        </form>
        <div className="flex items-center justify-center gap-2 mt-6 text-auth-muted">
          <LockKey size={14} weight="fill" className="text-auth-success" />
          <span className="text-[11px] uppercase tracking-widest">256-bit SSL Secured</span>
        </div>
      </div>
    </div>
  );
}
